// Client settings (persisted to localStorage).

import { BindMap, DEFAULT_BINDS } from "./binds";
import type { TextureMode } from "./render/renderer";
import { ruleDefaults } from "./rules";
import { GameMode, dumpMode, parseModeDump, presetMode } from "./modes";
import { defaultConfig, normalizeStore } from "./crosshair/config";
import type { CrosshairStore } from "./crosshair/types";
import {
  DEFAULT_WEAPON_SETTINGS,
  WEAPON_SETTINGS_VERSION,
  migrateWeaponPositionsV2,
  normalizeWeaponSettings,
  type WeaponSettings,
} from "./weapons/settings";
import { normalizeStore as normalizeHudLayout, type HudLayoutStore } from "./hud/types";

/**
 * Downstream snapshot rates the client may consume (Hz). Each is the 125 Hz sim
 * tick divided by an integer stride — 125 → 1, 62.5 → 2, 41.66 → 3, 31.25 → 4 —
 * so the client can emulate a server sending at that rate. Mirrors Diabotical's
 * "downstream tickrate" options.
 */
export const DOWNSTREAM_RATES = [125, 62.5, 41.66, 31.25] as const;

/** Hitmarker shape (Settings → Video → Hit feedback). */
export type HitmarkerStyle = "x" | "cross" | "dot";

/** Enemy silhouette appearance (Settings → Video). */
export interface EnemyVisualSettings {
  /** Outline hull size multiplier (1 = shipped). */
  outlineSize: number;
  /** Outline brightness multiplier (0 = off, >1 feeds bloom). */
  outlineBrightness: number;
  /** Halo size multiplier (1 = shipped). */
  haloSize: number;
  /** Halo brightness/opacity multiplier (0 = off). */
  haloBrightness: number;
}

/** Hit feedback appearance + volume (Settings → Video / Audio). */
export interface HitFeedbackSettings {
  hitmarker: boolean;
  hitmarkerStyle: HitmarkerStyle;
  hitmarkerSize: number;
  hitmarkerThickness: number;
  hitmarkerColor: string;
  hitmarkerKillColor: string;
  hitmarkerDuration: number;
  hitmarkerOpacity: number;
  damageNumbers: boolean;
  damageNumberSize: number;
  damageNumberColor: string;
  damageNumberKillColor: string;
  damageNumberDuration: number;
  damageNumberRise: number;
  damageNumberOpacity: number;
  /** Multiplier applied to the "hit" sound event (0..1+). */
  hitSoundVolume: number;
}

/** How the neon player-trail colours are chosen (Video settings). */
export type TrailColorMode = "custom" | "random" | "team";

/** Persisted player-trail appearance, sent to the renderer as one config. */
export interface TrailConfig {
  seconds: number;
  width: number;
  intensity: number;
  /** Colour scheme for you + teammates. */
  selfMode: TrailColorMode;
  selfColor: string;
  selfTeamColor: string;
  /** Colour scheme for enemies. */
  enemyMode: TrailColorMode;
  enemyColor: string;
  enemyTeamColor: string;
}

/** cgaz zone ids (also the style keys). */
export type CgazZone = "dead" | "partial" | "fast" | "turn" | "decel";

/** Appearance of the sampled cgaz strip (Settings → HUD → cgaz style). */
export interface CgazStyle {
  /** Overall canvas opacity (multiplies the per-zone alpha). */
  opacity: number;
  /** Strip height in px. */
  height: number;
  /** Central band width in px. */
  width: number;
  /** Dark backing behind the zones (0 = none). */
  bgOpacity: number;
  /** CSS drop shadow on the strip. */
  shadow: boolean;
  /** Outline around the strip. */
  border: boolean;
  borderWidth: number;
  borderColor: string;
  borderOpacity: number;
  /** Zone tones (hex). */
  colors: Record<CgazZone, string>;
  /** Per-zone alpha 0..1. */
  alphas: Record<CgazZone, number>;
}

export const DEFAULT_CGAZ_STYLE: CgazStyle = {
  opacity: 0.8,
  height: 12,
  width: 680,
  bgOpacity: 0.32,
  shadow: true,
  border: false,
  borderWidth: 1,
  borderColor: "#000000",
  borderOpacity: 0.6,
  colors: {
    dead: "#9696a5",
    partial: "#50dc78",
    fast: "#28d2eb",
    turn: "#ffbe3c",
    decel: "#ff5046",
  },
  alphas: {
    dead: 0.35,
    partial: 0.78,
    fast: 0.9,
    turn: 0.8,
    decel: 0.75,
  },
};

function cgazHex(v: unknown, fallback: string): string {
  return typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v) ? v.toLowerCase() : fallback;
}

/** Validate/clamp a persisted cgaz style. */
export function normalizeCgazStyle(raw: unknown): CgazStyle {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const colors = (o.colors && typeof o.colors === "object" ? o.colors : {}) as Record<string, unknown>;
  const alphas = (o.alphas && typeof o.alphas === "object" ? o.alphas : {}) as Record<string, unknown>;
  const num = (v: unknown, d: number, lo: number, hi: number): number => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : d;
  };
  const bool = (v: unknown, d: boolean): boolean => (typeof v === "boolean" ? v : d);
  const zone = (z: CgazZone): [string, number] => {
    const d = DEFAULT_CGAZ_STYLE;
    return [
      cgazHex(colors[z], d.colors[z]),
      num(alphas[z], d.alphas[z], 0, 1),
    ];
  };
  const c = {} as Record<CgazZone, string>;
  const a = {} as Record<CgazZone, number>;
  for (const z of ["dead", "partial", "fast", "turn", "decel"] as const) {
    const [col, alpha] = zone(z);
    c[z] = col;
    a[z] = alpha;
  }
  return {
    opacity: num(o.opacity, DEFAULT_CGAZ_STYLE.opacity, 0, 1),
    height: num(o.height, DEFAULT_CGAZ_STYLE.height, 2, 40),
    width: num(o.width, DEFAULT_CGAZ_STYLE.width, 160, 1600),
    bgOpacity: num(o.bgOpacity, DEFAULT_CGAZ_STYLE.bgOpacity, 0, 1),
    shadow: bool(o.shadow, DEFAULT_CGAZ_STYLE.shadow),
    border: bool(o.border, DEFAULT_CGAZ_STYLE.border),
    borderWidth: num(o.borderWidth, DEFAULT_CGAZ_STYLE.borderWidth, 1, 6),
    borderColor: cgazHex(o.borderColor, DEFAULT_CGAZ_STYLE.borderColor),
    borderOpacity: num(o.borderOpacity, DEFAULT_CGAZ_STYLE.borderOpacity, 0, 1),
    colors: c,
    alphas: a,
  };
}

function defaultCgazStyle(): CgazStyle {
  return {
    ...DEFAULT_CGAZ_STYLE,
    colors: { ...DEFAULT_CGAZ_STYLE.colors },
    alphas: { ...DEFAULT_CGAZ_STYLE.alphas },
  };
}

export interface HudSettings {
  /** cgaz strip appearance. Position/visibility live in `hudLayout`. */
  cgazStyle: CgazStyle;
}

export const DEFAULT_HUD_SETTINGS: HudSettings = {
  cgazStyle: defaultCgazStyle(),
};

export interface Settings {
  fov: number;
  sensitivity: number;
  /**
   * Panini warp strength (composited 3D pass only). 0 = plain rectilinear;
   * 1 = classic Panini. Fractional values blend the two (see `docs/JOURNAL.md`).
   */
  panini: number;
  /** Legacy single-crosshair fields (kept for config import/migration). */
  crosshairColor: string;
  crosshairSize: number;
  /** Layered crosshair slots (see `docs/crosshair-plan.md`). */
  crosshair: CrosshairStore;
  crosshairVersion: number;
  /** Per-weapon client settings (see `docs/weapon-settings-plan.md`). */
  weaponSettings: WeaponSettings;
  weaponVersion: number;
  /** cgaz strip appearance (position/visibility live in `hudLayout`). */
  hud: HudSettings;
  /** HUD element layout + value colours (see `docs/hud-plan.md`). */
  hudLayout: HudLayoutStore;
  binds: BindMap;
  /** Bumped when the default bind layout should be re-applied once. */
  bindVersion: number;
  /** Texture filtering quality. */
  textureMode: TextureMode;
  /** Master sound volume, 0..1. */
  volume: number;
  /**
   * `cg_truelightning` (0..1): the LG beam is anchored to the muzzle; this sets
   * how well the far end follows the crosshair — 1 = perfect, 0 = the server's
   * real (lagged) impact.
   */
  cgTrueLightning: number;
  /** Neon trail lifetime in seconds (0 = off). */
  trailSeconds: number;
  /** Neon trail ribbon half-width (world units). */
  trailWidth: number;
  /** Neon trail brightness multiplier (feeds bloom). */
  trailIntensity: number;
  /** One-time trail-defaults migration version. */
  trailVersion: number;
  /** Self/team colour scheme. */
  trailSelfMode: TrailColorMode;
  trailSelfColor: string;
  trailSelfTeamColor: string;
  /** Enemy colour scheme (also drives the enemy model). */
  trailEnemyMode: TrailColorMode;
  trailEnemyColor: string;
  trailEnemyTeamColor: string;
  /** Enemy silhouette appearance (outline/halo). */
  enemyVisual: EnemyVisualSettings;
  /** Hitmarker, damage numbers and hit-sound volume. */
  hitFeedback: HitFeedbackSettings;
  /** Sound event id -> file name under `snd/` (or absent for none). */
  sounds: Record<string, string>;
  /** Bumped when new default sound assignments should be re-applied once. */
  soundVersion: number;
  /** Dev/tuning rules (key -> value), applied to the server. */
  rules: Record<string, number>;
  /** Chosen rules preset id (see `rules.ts` `PRESETS`). */
  rulePreset: string;
  /** Current game mode (match rules). */
  mode: GameMode;
  /** Chosen mode preset id (see `modes.ts` `MODE_PRESETS`). */
  modePreset: string;
  /** Downstream snapshot rate (Hz) the client consumes (see DOWNSTREAM_RATES). */
  downstreamHz: number;
  /** Bumped when the downstream default changes (one-time migration). */
  snapshotVersion: number;
  /** Bumped when the shipped FOV/projection defaults change (one-time). */
  viewDefaultsVersion: number;
  /** Global VFX colour overrides (hex). */
  vfxColors: {
    explosion: string;
    sparks: string;
    muzzle: string;
    smoke: string;
  };
  /** Bumped when a VFX colour default changes (one-time migration). */
  vfxColorVersion: number;
  /** Per-weapon projectile/beam colour overrides (weapon id -> hex). */
  projectileColors: Record<string, string>;
  /** Per-weapon projectile/orb opacity overrides (weapon id -> 0..1). */
  projectileAlpha: Record<string, number>;
}

/** Hex-colour fields (used to validate stored colours). */
export const VFX_COLOR_KEYS = ["explosion", "sparks", "muzzle", "smoke"] as const;

/** Default VFX colours. */
export const DEFAULT_VFX_COLORS: Record<(typeof VFX_COLOR_KEYS)[number], string> = {
  explosion: "#a3e635", // lime
  sparks: "#22d3ee", // cyan
  muzzle: "#f22f2f", // red
  smoke: "#ffffff", // neutral white
};

const HEX_RE = /^#[0-9a-f]{6}$/i;
const isHex = (v: unknown): v is string => typeof v === "string" && HEX_RE.test(v);

/** Validate a stored trail colour mode (new or legacy). */
function trailModeOf(v: unknown, fallback: TrailColorMode): TrailColorMode {
  return v === "custom" || v === "random" || v === "team" ? v : fallback;
}

const KEY = "webrace.settings";

const TEXTURE_MODES: TextureMode[] = ["nearest", "bilinear", "trilinear", "anisotropic"];

/** Bump to force the defaults below onto existing installs (one time). */
const SOUND_VERSION = 4;

/** Bind-layout version (see the migration in `loadSettings`). */
const BIND_VERSION = 10;

/** Downstream-default version. v1: move the old 62.5 default to 125. */
const SNAPSHOT_VERSION = 1;

/**
 * View-default version.
 * v1: the 130/panini experiment -> Quake-normal 90/rect.
 * v2: `projection` enum -> continuous `panini` strength (0 = rectilinear).
 */
const VIEW_DEFAULTS_VERSION = 2;

/** VFX-default version. v1: the old tan smoke default becomes white. */
const VFX_COLOR_VERSION = 1;

/**
 * v6 bind layout (the Quake-style weapon keys in the reference layout).
 *
 * Each entry moves one action from its old default codes to its new ones. The
 * migration is per-action and two-phase so custom binds survive it:
 *   1. remove the old codes while they still belong to that action;
 *   2. add the new codes only when the action has no binding left (a custom
 *      bind wins) and the code is free.
 */
const BIND_V6_CHANGES: { action: string; old: string[]; next: string[] }[] = [
  { action: "attack2", old: ["Mouse1"], next: ["Mouse4"] },
  { action: "weapon_next", old: ["KeyE"], next: [] },
  { action: "weapon_prev", old: ["KeyR"], next: [] },
  { action: "weapon_rocket", old: [], next: ["KeyF"] },
  { action: "weapon_plasma", old: [], next: ["KeyX"] },
  { action: "weapon_lightning", old: [], next: ["KeyE"] },
  { action: "weapon_railgun", old: [], next: ["KeyR"] },
  { action: "weapon_shotgun", old: [], next: ["KeyC"] },
  { action: "weapon_melee", old: [], next: ["Mouse1"] },
  { action: "weapon_experimental", old: ["KeyC"], next: ["KeyQ"] },
  { action: "weapon_ball", old: ["KeyQ"], next: [] },
  { action: "recall_prev", old: ["BracketLeft", "WheelDown"], next: ["BracketLeft", "WheelUp"] },
  { action: "recall_next", old: ["WheelUp", "BracketRight"], next: ["WheelDown", "BracketRight"] },
  { action: "spectate_next", old: ["KeyX"], next: ["KeyP"] },
];

/**
 * Fill in defaults for actions that have **no** binding at all.
 *
 * Only used while upgrading an older bind map: a current-version saved map is
 * taken verbatim so an action the player deliberately unbound stays unbound
 * (the old "merge defaults by code" approach silently brought defaults back).
 */
function addMissingBinds(b: BindMap): void {
  const groups = new Map<string, string[]>();
  for (const [code, action] of Object.entries(DEFAULT_BINDS)) {
    const list = groups.get(action) ?? [];
    list.push(code);
    groups.set(action, list);
  }
  const bound = new Set(Object.values(b));
  for (const [action, codes] of groups) {
    if (bound.has(action)) continue;
    for (const code of codes) {
      if (!(code in b)) {
        b[code] = action;
        bound.add(action);
      }
    }
  }
}

function migrateBindsToV6(b: BindMap): void {
  // Phase 1: drop the old codes while they still belong to their old action.
  for (const change of BIND_V6_CHANGES) {
    for (const code of change.old) {
      if (b[code] === change.action) delete b[code];
    }
  }
  // Phase 2: add the new codes only for actions left with no binding (a custom
  // bind wins) and only when the code is free.
  for (const change of BIND_V6_CHANGES) {
    if (Object.values(b).includes(change.action)) continue;
    for (const code of change.next) {
      if (!(code in b)) b[code] = change.action;
    }
  }
}

function migrateBindsToV8(b: BindMap): void {
  // Wheel was recall next/prev. Move the stock binds to ping / machinegun and
  // keep recall reachable on the brackets if the wheel was the only binding.
  if (b.WheelUp === "recall_prev") b.WheelUp = "ping";
  if (b.WheelDown === "recall_next") b.WheelDown = "weapon_machinegun";
  if (!Object.values(b).includes("recall_prev") && !b.BracketLeft) b.BracketLeft = "recall_prev";
  if (!Object.values(b).includes("recall_next") && !b.BracketRight) b.BracketRight = "recall_next";
}

function migrateBindsToV9(b: BindMap): void {
  // F8 was the playtest mark. Move the stock bind to F9 and give F8 to the
  // screenshot action; a custom F8 binding is left alone.
  if (b.F8 === "mark") {
    delete b.F8;
    if (!Object.values(b).includes("mark") && !b.F9) b.F9 = "mark";
    if (!("F8" in b)) b.F8 = "screenshot";
  }
}

function migrateBindsToV7(b: BindMap): void {
  // Crouch and the grenade launcher were the wrong way round in every earlier
  // layout (crouch LCtrl, grenade LShift). Swap only while both still hold the
  // old defaults, so a deliberate custom layout is never rewritten.
  if (b["ShiftLeft"] === "weapon_grenade" && b["ControlLeft"] === "crouch") {
    b["ShiftLeft"] = "crouch";
    b["ControlLeft"] = "weapon_grenade";
  }
}

const DEFAULT_ENEMY_VISUAL: EnemyVisualSettings = {
  outlineSize: 1.15,
  outlineBrightness: 1.25,
  haloSize: 1.2,
  haloBrightness: 1.25,
};

const DEFAULT_HIT_FEEDBACK: HitFeedbackSettings = {
  hitmarker: true,
  hitmarkerStyle: "x",
  hitmarkerSize: 30,
  hitmarkerThickness: 2.5,
  hitmarkerColor: "#ffffff",
  hitmarkerKillColor: "#ff4040",
  hitmarkerDuration: 200,
  hitmarkerOpacity: 1,
  damageNumbers: true,
  damageNumberSize: 20,
  damageNumberColor: "#ffffff",
  damageNumberKillColor: "#ff4040",
  damageNumberDuration: 850,
  damageNumberRise: 52,
  damageNumberOpacity: 1,
  hitSoundVolume: 1,
};

function numIn(v: unknown, d: number, lo: number, hi: number): number {
  return typeof v === "number" && Number.isFinite(v) ? clamp(v, lo, hi) : d;
}

function normalizeEnemyVisual(raw: unknown): EnemyVisualSettings {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    outlineSize: numIn(o.outlineSize, DEFAULT_ENEMY_VISUAL.outlineSize, 0.5, 3),
    outlineBrightness: numIn(o.outlineBrightness, DEFAULT_ENEMY_VISUAL.outlineBrightness, 0, 2),
    haloSize: numIn(o.haloSize, DEFAULT_ENEMY_VISUAL.haloSize, 0.5, 3),
    haloBrightness: numIn(o.haloBrightness, DEFAULT_ENEMY_VISUAL.haloBrightness, 0, 2),
  };
}

function normalizeHitFeedback(raw: unknown): HitFeedbackSettings {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const hex = (v: unknown, d: string) => (isHex(v) ? v.toLowerCase() : d);
  return {
    hitmarker: typeof o.hitmarker === "boolean" ? o.hitmarker : DEFAULT_HIT_FEEDBACK.hitmarker,
    hitmarkerStyle: o.hitmarkerStyle === "cross" || o.hitmarkerStyle === "dot" ? o.hitmarkerStyle : "x",
    hitmarkerSize: numIn(o.hitmarkerSize, DEFAULT_HIT_FEEDBACK.hitmarkerSize, 6, 64),
    hitmarkerThickness: numIn(o.hitmarkerThickness, DEFAULT_HIT_FEEDBACK.hitmarkerThickness, 1, 8),
    hitmarkerColor: hex(o.hitmarkerColor, DEFAULT_HIT_FEEDBACK.hitmarkerColor),
    hitmarkerKillColor: hex(o.hitmarkerKillColor, DEFAULT_HIT_FEEDBACK.hitmarkerKillColor),
    hitmarkerDuration: numIn(o.hitmarkerDuration, DEFAULT_HIT_FEEDBACK.hitmarkerDuration, 40, 600),
    hitmarkerOpacity: numIn(o.hitmarkerOpacity, DEFAULT_HIT_FEEDBACK.hitmarkerOpacity, 0.1, 1),
    damageNumbers: typeof o.damageNumbers === "boolean" ? o.damageNumbers : DEFAULT_HIT_FEEDBACK.damageNumbers,
    damageNumberSize: numIn(o.damageNumberSize, DEFAULT_HIT_FEEDBACK.damageNumberSize, 8, 64),
    damageNumberColor: hex(o.damageNumberColor, DEFAULT_HIT_FEEDBACK.damageNumberColor),
    damageNumberKillColor: hex(o.damageNumberKillColor, DEFAULT_HIT_FEEDBACK.damageNumberKillColor),
    damageNumberDuration: numIn(o.damageNumberDuration, DEFAULT_HIT_FEEDBACK.damageNumberDuration, 200, 2000),
    damageNumberRise: numIn(o.damageNumberRise, DEFAULT_HIT_FEEDBACK.damageNumberRise, 0, 200),
    damageNumberOpacity: numIn(o.damageNumberOpacity, DEFAULT_HIT_FEEDBACK.damageNumberOpacity, 0.1, 1),
    hitSoundVolume: numIn(o.hitSoundVolume, DEFAULT_HIT_FEEDBACK.hitSoundVolume, 0, 2),
  };
}

const DEFAULTS: Settings = {
  fov: 90,
  sensitivity: 2.0,
  panini: 0,
  crosshairColor: "#fa00ff",
  crosshairSize: 18,
  crosshair: normalizeStore(undefined),
  crosshairVersion: 1,
  weaponSettings: normalizeWeaponSettings(DEFAULT_WEAPON_SETTINGS),
  weaponVersion: WEAPON_SETTINGS_VERSION,
  hud: { ...DEFAULT_HUD_SETTINGS },
  hudLayout: normalizeHudLayout(undefined),
  binds: { ...DEFAULT_BINDS },
  bindVersion: BIND_VERSION,
  textureMode: "anisotropic",
  volume: 0.8,
  cgTrueLightning: 1,
  trailSeconds: 1.0,
  trailWidth: 4.2,
  trailIntensity: 1.0,
  trailVersion: 1,
  trailSelfMode: "custom",
  trailSelfColor: "#ff6a1a",
  trailSelfTeamColor: "#22d3ee",
  trailEnemyMode: "team",
  trailEnemyColor: "#ff3b30",
  trailEnemyTeamColor: "#ff3b30",
  enemyVisual: { ...DEFAULT_ENEMY_VISUAL },
  hitFeedback: { ...DEFAULT_HIT_FEEDBACK },
  sounds: {
    jump: "FS Ground Civilian Walk N05.wav",
    dash: "ljud3.wav",
    walljump: "FS Ground Civilian Walk N03.wav",
    ping: "soundshelfstudio-ui-hyperdrive-ready-ping-537581.mp3",
  },
  soundVersion: SOUND_VERSION,
  rules: ruleDefaults(),
  rulePreset: "warfork",
  mode: presetMode("race"),
  modePreset: "race",
  downstreamHz: 125,
  snapshotVersion: SNAPSHOT_VERSION,
  viewDefaultsVersion: VIEW_DEFAULTS_VERSION,
  vfxColors: { ...DEFAULT_VFX_COLORS },
  vfxColorVersion: VFX_COLOR_VERSION,
  projectileColors: {},
  projectileAlpha: {},
};

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

/** Load settings from localStorage (with validation/clamping). */
/** Field specs for the flat (primitive) settings. Nested objects have their
 * own normalizers below; everything goes through `normalizeSettings`. */
type FieldSpec =
  | { kind: "number"; min: number; max: number; coerce?: boolean; nonZero?: boolean }
  | { kind: "string"; max?: number }
  | { kind: "hex" }
  | { kind: "enum"; values: readonly string[] };

const PRIMITIVE_FIELDS: Record<string, FieldSpec> = {
  fov: { kind: "number", min: 60, max: 160, coerce: true, nonZero: true },
  sensitivity: { kind: "number", min: 0.1, max: 50, coerce: true, nonZero: true },
  crosshairColor: { kind: "hex" },
  crosshairSize: { kind: "number", min: 4, max: 64, coerce: true, nonZero: true },
  textureMode: { kind: "enum", values: TEXTURE_MODES },
  panini: { kind: "number", min: 0, max: 3 },
  volume: { kind: "number", min: 0, max: 1 },
  cgTrueLightning: { kind: "number", min: 0, max: 1 },
  trailSeconds: { kind: "number", min: 0, max: 3 },
  trailWidth: { kind: "number", min: 0.5, max: 8 },
  trailIntensity: { kind: "number", min: 0.1, max: 2 },
  trailSelfColor: { kind: "hex" },
  trailSelfTeamColor: { kind: "hex" },
  trailEnemyColor: { kind: "hex" },
  trailEnemyTeamColor: { kind: "hex" },
  rulePreset: { kind: "string" },
  modePreset: { kind: "string" },
};

/** Deep-ish default settings (nested objects are fresh). */
function defaultSettings(): Settings {
  return {
    ...DEFAULTS,
    binds: { ...DEFAULT_BINDS },
    bindVersion: BIND_VERSION,
    sounds: { ...DEFAULTS.sounds },
    rules: { ...DEFAULTS.rules },
    mode: { ...DEFAULTS.mode },
    enemyVisual: { ...DEFAULTS.enemyVisual },
    hitFeedback: { ...DEFAULTS.hitFeedback },
    vfxColors: { ...DEFAULTS.vfxColors },
    projectileColors: { ...DEFAULTS.projectileColors },
    projectileAlpha: { ...DEFAULTS.projectileAlpha },
    crosshair: normalizeStore(DEFAULTS.crosshair),
    weaponSettings: normalizeWeaponSettings(DEFAULTS.weaponSettings),
    hud: {
      ...DEFAULTS.hud,
      cgazStyle: normalizeCgazStyle(DEFAULTS.hud.cgazStyle),
    },
    hudLayout: normalizeHudLayout(DEFAULTS.hudLayout),
  };
}

function applyPrimitiveFields(o: Record<string, unknown>, s: Settings): void {
  for (const [key, spec] of Object.entries(PRIMITIVE_FIELDS)) {
    const v = o[key];
    if (v === undefined || v === null) continue;
    let value: unknown;
    if (spec.kind === "number") {
      const n = typeof v === "number" ? v : spec.coerce ? Number(v) : NaN;
      if (!Number.isFinite(n) || (spec.nonZero && n === 0)) continue;
      value = clamp(n, spec.min, spec.max);
    } else if (spec.kind === "string") {
      if (typeof v !== "string") continue;
      value = spec.max ? v.slice(0, spec.max) : v;
    } else if (spec.kind === "hex") {
      if (!isHex(v)) continue;
      value = v.toLowerCase();
    } else {
      if (typeof v !== "string" || !spec.values.includes(v)) continue;
      value = v;
    }
    (s as unknown as Record<string, unknown>)[key] = value;
  }
}

function migrateCrosshair(o: Record<string, unknown>, s: Settings): void {
  if (o.crosshair && typeof o.crosshair === "object") {
    s.crosshair = normalizeStore(o.crosshair);
  } else {
    // Older installs only have colour + size: rebuild their exact look as
    // layer 1 (1 px arms of size/2, black 1 px outline) and keep it as the
    // default slot.
    const cfg = defaultConfig();
    const layer = cfg.layers[0];
    layer.color = isHex(o.crosshairColor) ? o.crosshairColor : layer.color;
    const size = clamp(Number(o.crosshairSize) || DEFAULTS.crosshairSize, 4, 64);
    layer.params.length = size / 2;
    layer.params.thickness = 1;
    layer.outline = { color: "#000000", thickness: 1, mode: "scale" };
    s.crosshair = {
      version: 1,
      slots: [{ id: "default", name: "Default", config: cfg }],
      activeId: "default",
      defaultId: "default",
    };
  }
  s.crosshairVersion = 1;
}

function migrateWeaponSettings(o: Record<string, unknown>, s: Settings): void {
  if (o.weaponSettings && typeof o.weaponSettings === "object") {
    s.weaponSettings = normalizeWeaponSettings(o.weaponSettings);
  }
  const version = typeof o.weaponVersion === "number" ? o.weaponVersion : 0;
  if (version < WEAPON_SETTINGS_VERSION) migrateWeaponPositionsV2(s.weaponSettings);
  s.weaponVersion = WEAPON_SETTINGS_VERSION;
}

function migrateHud(o: Record<string, unknown>, s: Settings): void {
  if (o.hudLayout && typeof o.hudLayout === "object") {
    s.hudLayout = normalizeHudLayout(o.hudLayout);
  }
  if (!o.hud || typeof o.hud !== "object") return;
  const h = o.hud as Record<string, unknown>;
  if (h.cgazStyle) s.hud.cgazStyle = normalizeCgazStyle(h.cgazStyle);
  // Older builds stored movement-aid visibility/offsets separately. Fold them
  // into the one HUD layout document (playing + spectate) so there is a single
  // position/visibility model.
  const aids: { key: string; ids: string[] }[] = [
    { key: "angles", ids: ["angle-hud"] },
    { key: "accel", ids: ["accel-bar", "speed"] },
    { key: "cgaz", ids: ["cgaz-hud"] },
  ];
  const offs = (h.offsets && typeof h.offsets === "object" ? h.offsets : {}) as Record<string, unknown>;
  for (const aid of aids) {
    const mode = h[aid.key];
    if (mode !== "on" && mode !== "off" && mode !== "race") continue;
    const rawOff = offs[aid.key] as Record<string, unknown> | undefined;
    const x = rawOff && typeof rawOff.x === "number" ? clamp(rawOff.x, -500, 500) : 0;
    const y = rawOff && typeof rawOff.y === "number" ? clamp(rawOff.y, -500, 500) : 0;
    for (const doc of [s.hudLayout.playing, s.hudLayout.spectate]) {
      for (const el of doc.elements) {
        if (!aid.ids.includes(el.id)) continue;
        el.visible = mode !== "off";
        el.raceOnly = mode === "race";
        el.anchor = "auto";
        el.offset = { x, y };
      }
    }
  }
}

function migrateTrail(o: Record<string, unknown>, s: Settings): void {
  // One-time default bump: installs still on the old shipped look
  // (1.5 s / 3.2 width / 0.6 glow) adopt 1.0 / 4.2 / 1.0. Custom values stay.
  const version = typeof o.trailVersion === "number" ? o.trailVersion : 0;
  if (
    version < 1 &&
    s.trailSeconds === 1.5 &&
    s.trailWidth === 3.2 &&
    s.trailIntensity === 0.6
  ) {
    s.trailSeconds = 1.0;
    s.trailWidth = 4.2;
    s.trailIntensity = 1.0;
  }
  s.trailVersion = 1;
  // Migrate the single legacy trail colour to self+enemy when the new fields
  // are absent; fresh installs get the new defaults.
  const hadOld = o.trailColorMode !== undefined || o.trailColor !== undefined;
  const oldMode = hadOld ? trailModeOf(o.trailColorMode, "custom") : undefined;
  const oldColor = hadOld && isHex(o.trailColor) ? o.trailColor : undefined;
  s.trailSelfMode = trailModeOf(o.trailSelfMode, trailModeOf(oldMode, DEFAULTS.trailSelfMode));
  s.trailEnemyMode = trailModeOf(o.trailEnemyMode, trailModeOf(oldMode, DEFAULTS.trailEnemyMode));
  if (!isHex(o.trailSelfColor)) s.trailSelfColor = oldColor ?? DEFAULTS.trailSelfColor;
  if (!isHex(o.trailEnemyColor)) s.trailEnemyColor = oldColor ?? DEFAULTS.trailEnemyColor;
}

function migrateBinds(o: Record<string, unknown>, s: Settings): void {
  if (!o.binds || typeof o.binds !== "object") return;
  // Start from the saved map verbatim. Defaults are only added while upgrading
  // an older map, never on every load (a deliberate unbind must survive).
  s.binds = { ...(o.binds as BindMap) };
  // Quake-canonical action ids: migrate saved Warfork role names.
  const renamed: Record<string, string> = {
    weapon_riotgun: "weapon_shotgun",
    weapon_laser: "weapon_lightning",
    weapon_electrobolt: "weapon_railgun",
    weapon_gunblade: "weapon_melee",
  };
  for (const [code, action] of Object.entries(s.binds)) {
    const next = renamed[action];
    if (next) s.binds[code] = next;
  }
  const version = typeof o.bindVersion === "number" ? o.bindVersion : 0;
  // v2-v5 (one-time): ball launcher on Q, grenade launcher on LShift, weapon_prev on R.
  if (version < 5) {
    for (const action of ["weapon_ball", "weapon_grenade", "weapon_prev"]) {
      for (const [code, a] of Object.entries(s.binds)) {
        if (a === action) delete s.binds[code];
      }
    }
    s.binds["KeyQ"] = "weapon_ball";
    s.binds["ShiftLeft"] = "weapon_grenade";
    s.binds["KeyR"] = "weapon_prev";
    for (const [code, a] of Object.entries(s.binds)) {
      if (a === "weeball" || (code === "KeyG" && a === "ghost")) delete s.binds[code];
    }
    if (!s.binds["KeyG"]) s.binds["KeyG"] = "weeball";
    if (!s.binds["KeyH"]) s.binds["KeyH"] = "ghost";
    if (!s.binds["Tab"]) s.binds["Tab"] = "scoreboard";
    if (!s.binds["F3"]) s.binds["F3"] = "ready";
    if (!s.binds["KeyX"]) s.binds["KeyX"] = "spectate_next";
    if (!s.binds["KeyZ"]) s.binds["KeyZ"] = "spectate_prev";
    if (!s.binds["KeyV"]) s.binds["KeyV"] = "spectate_mode";
  }
  if (version < 6) migrateBindsToV6(s.binds);
  if (version < 7) migrateBindsToV7(s.binds);
  if (version < 8) migrateBindsToV8(s.binds);
  if (version < 9) migrateBindsToV9(s.binds);
  if (version < BIND_VERSION) addMissingBinds(s.binds);
  s.bindVersion = BIND_VERSION;
}

function migrateSounds(o: Record<string, unknown>, s: Settings): void {
  if (o.sounds && typeof o.sounds === "object") {
    for (const [k, v] of Object.entries(o.sounds as Record<string, unknown>)) {
      if (typeof v === "string" && v) s.sounds[k] = v;
    }
  }
  const version = typeof o.soundVersion === "number" ? o.soundVersion : 0;
  if (version < 3) {
    s.sounds.jump = DEFAULTS.sounds.jump;
    s.sounds.dash = DEFAULTS.sounds.dash;
    s.sounds.walljump = DEFAULTS.sounds.walljump;
  }
  if (version < 4 && (!s.sounds.ping || s.sounds.ping === "ljud2.wav")) {
    s.sounds.ping = DEFAULTS.sounds.ping;
  }
  if (!s.sounds.ping) s.sounds.ping = DEFAULTS.sounds.ping;
  s.soundVersion = SOUND_VERSION;
}

function migrateRules(o: Record<string, unknown>, s: Settings): void {
  if (!o.rules || typeof o.rules !== "object") return;
  // Merge onto defaults so new knobs appear with their defaults.
  s.rules = { ...ruleDefaults() };
  const prefixes: [string, string][] = [
    ["riotgun_", "shotgun_"],
    ["laser_", "lightning_"],
    ["electrobolt_", "railgun_"],
    ["gunblade_", "melee_"],
  ];
  for (const [k, v] of Object.entries(o.rules as Record<string, unknown>)) {
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    const hit = prefixes.find(([old]) => k.startsWith(old));
    s.rules[hit ? hit[1] + k.slice(hit[0].length) : k] = v;
  }
}

function migrateMode(o: Record<string, unknown>, s: Settings): void {
  if (typeof o.mode === "string") s.mode = parseModeDump(o.mode);
  else if (o.mode && typeof o.mode === "object") {
    s.mode = parseModeDump(dumpMode(o.mode as GameMode));
  }
}

function migrateDownstream(o: Record<string, unknown>, s: Settings): void {
  const hz = Number(o.downstreamHz);
  if ((DOWNSTREAM_RATES as readonly number[]).includes(hz)) s.downstreamHz = hz;
  // v1: 62.5 was the temporary default right after the 125 Hz migration; move
  // those installs to 125/125. Keep deliberate 41.66/31.25 choices.
  const version = typeof o.snapshotVersion === "number" ? o.snapshotVersion : 0;
  if (version < SNAPSHOT_VERSION && s.downstreamHz === 62.5) s.downstreamHz = 125;
  s.snapshotVersion = SNAPSHOT_VERSION;
}

function migrateViewDefaults(o: Record<string, unknown>, s: Settings): void {
  const version = typeof o.viewDefaultsVersion === "number" ? o.viewDefaultsVersion : 0;
  // Old configs only have the enum; new ones own `panini` directly.
  let legacyPanini = o.panini === undefined && o.projection === "panini";
  if (version < 1) {
    // v1: the previous shipped default was the wide-FOV Panini experiment.
    // Move installs still on exactly that pair to Quake-normal 90°/rectilinear;
    // a deliberate custom value is left alone.
    if (s.fov === 130 && legacyPanini) {
      s.fov = 90;
      s.panini = 0;
      legacyPanini = false; // already migrated away; don't re-enable below
    }
  }
  if (version < 2) {
    // v2: the projection enum became a continuous strength. Installs that had
    // Panini switched on keep it (classic d = 1); rectilinear stays off.
    if (legacyPanini) s.panini = 1;
  }
  s.viewDefaultsVersion = VIEW_DEFAULTS_VERSION;
}

function migrateVfx(o: Record<string, unknown>, s: Settings): void {
  if (o.vfxColors && typeof o.vfxColors === "object") {
    for (const k of VFX_COLOR_KEYS) {
      const v = (o.vfxColors as Record<string, unknown>)[k];
      if (isHex(v)) s.vfxColors[k] = v;
    }
  }
  // v1: tan was the shipped smoke colour; move unchanged installs to white.
  const version = typeof o.vfxColorVersion === "number" ? o.vfxColorVersion : 0;
  if (version < VFX_COLOR_VERSION && s.vfxColors.smoke.toLowerCase() === "#e0895c") {
    s.vfxColors.smoke = DEFAULT_VFX_COLORS.smoke;
  }
  s.vfxColorVersion = VFX_COLOR_VERSION;
}

function migrateProjectiles(o: Record<string, unknown>, s: Settings): void {
  if (o.projectileColors && typeof o.projectileColors === "object") {
    for (const [k, v] of Object.entries(o.projectileColors as Record<string, unknown>)) {
      if (isHex(v)) s.projectileColors[k] = v;
    }
  }
  if (o.projectileAlpha && typeof o.projectileAlpha === "object") {
    for (const [k, v] of Object.entries(o.projectileAlpha as Record<string, unknown>)) {
      if (typeof v === "number" && Number.isFinite(v)) s.projectileAlpha[k] = clamp(v, 0, 1);
    }
  }
}

/**
 * Validate + migrate a persisted settings blob. Shared by localStorage load and
 * share-code import, so both paths get the same defaults, clamps and migrations.
 */
export function normalizeSettings(raw: unknown): Settings {
  const s = defaultSettings();
  if (!raw || typeof raw !== "object") return s;
  const o = raw as Record<string, unknown>;
  applyPrimitiveFields(o, s);
  s.enemyVisual = normalizeEnemyVisual(o.enemyVisual);
  s.hitFeedback = normalizeHitFeedback(o.hitFeedback);
  migrateCrosshair(o, s);
  migrateWeaponSettings(o, s);
  migrateHud(o, s);
  migrateTrail(o, s);
  migrateBinds(o, s);
  migrateSounds(o, s);
  migrateRules(o, s);
  migrateMode(o, s);
  migrateDownstream(o, s);
  migrateViewDefaults(o, s);
  migrateVfx(o, s);
  migrateProjectiles(o, s);
  return s;
}

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? normalizeSettings(JSON.parse(raw)) : defaultSettings();
  } catch {
    return defaultSettings();
  }
}

/** Persist settings to localStorage. */
export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

/**
 * The single owner of the persisted `Settings` object.
 *
 * The menu mutates `store.value()` in place and calls `commit(false)` (it has
 * already applied the change to its controls). Command-line / programmatic
 * changes call `commit()` so subscribers can re-apply the whole UI. Nothing
 * else writes localStorage.
 */
export interface SettingsStore {
  value(): Settings;
  /** Persist + optionally notify subscribers. */
  commit(notify?: boolean): void;
  /** Replace the whole object (share-code import), persist, optionally notify. */
  replace(next: Settings, notify?: boolean): void;
  subscribe(fn: (s: Settings) => void): () => void;
}

export function createSettingsStore(initial?: Settings): SettingsStore {
  let current = initial ?? loadSettings();
  const listeners = new Set<(s: Settings) => void>();
  const notify = (): void => {
    for (const fn of listeners) fn(current);
  };
  return {
    value: () => current,
    commit: (notifyListeners = true) => {
      saveSettings(current);
      if (notifyListeners) notify();
    },
    replace: (next, notifyListeners = true) => {
      current = next;
      saveSettings(current);
      if (notifyListeners) notify();
    },
    subscribe: (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}

/** Encode settings into a compact share code (base64 of JSON). */
export function encodeConfig(s: Settings): string {
  try {
    const json = JSON.stringify(s);
    // Encode to base64 URL-safe.
    const bytes = new TextEncoder().encode(json);
    let bin = "";
    bytes.forEach((b) => (bin += String.fromCharCode(b)));
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch {
    return "";
  }
}

/** Decode a share code back into settings (partial merge onto defaults). */
export function decodeConfig(code: string): Settings | null {
  try {
    const b64 = code.replace(/-/g, "+").replace(/_/g, "/");
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== "object") return null;
    return normalizeSettings(parsed);
  } catch {
    return null;
  }
}

export { DEFAULTS };
